import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core'
import { FormBuilder, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms'
import type { Property } from '@app/shared/models/common-models'
import type { PropertyChangeArgs } from '@app/shared/models/entity-update.model'
import { EntityService } from '@app/shared/services/entity.service'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { LocalizeNumberPropertyPipe } from '@app/shared/pipes/localize-number-property/localize-number-property.pipe'
import { getLocaleNumberSymbol, NumberSymbol } from '@angular/common'
import { TranslateService } from '@ngx-translate/core'

/*
 * Note about the number handling:
 *
 * Customer wants the input field values to be localized in the application, so instead of a number field we have to use
 * a text input field. This component contains relevant logic for:
 * - Formatting the number into the field
 * - Parsing values the user inputs to the fields
 * The users current language will be used for choosing the appropriate thousand and decimal separators.
 *
 * Relevant JIRA ticket: https://jira.magicad.com/browse/FLEXIT2-622
 */

@UntilDestroy()
@Component({
  selector: 'app-entity-number-property',
  templateUrl: './number-property.component.html',
  styleUrls: ['./number-property.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class NumberPropertyComponent implements OnChanges, OnInit, OnDestroy {
  @Input() property: Property

  @Input() formGroup: FormGroup

  @Input() showMeasurementUnit = true

  @Input() showLabel = true

  @Input() showErrors = true

  @Output() propertyChange = new EventEmitter<PropertyChangeArgs>()

  propertyForm: FormControl<string>

  toolTipTextId: string

  private hasFocus: boolean

  /**
   * A simple cache for optimizing the number parsing. The optimization is required as the input value must be parsed
   * by the min/max validators each time anything in the UI updates.
   * This cache is cleared when language is changed
   */
  private parsedValueCache: Record<string, number> = {}

  constructor(
    private entityService: EntityService,
    private localizeNumberPropertyPipe: LocalizeNumberPropertyPipe,
    private translateService: TranslateService,
    private formBuilder: FormBuilder,
  ) {
    this.propertyForm = this.formBuilder.control<string>(null, { updateOn: 'blur' })
  }

  ngOnInit(): void {
    this.propertyForm.valueChanges.pipe(untilDestroyed(this)).subscribe(value => {
      if (this.propertyForm.invalid) {
        return
      }

      // Parse the input number value assuming the thousand and decimal separators of the currently used language
      // The supported languages are registered in ~/app/locale-registration.ts
      const numberValue = this.parseNumberInput(value)

      const event: PropertyChangeArgs = {
        Key: this.property.Key,
        Value: value === null || value === undefined || Number.isNaN(numberValue) ? null : numberValue,
        MeasurementUnitKey: this.property.MeasurementUnit,
      }
      this.propertyChange.emit(event)
    })

    // Refresh input on language change
    this.translateService.onLangChange.pipe(untilDestroyed(this)).subscribe(() => {
      this.parsedValueCache = {}
      this.updatePropertyForm()
    })
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      this.property &&
      changes.formGroup &&
      changes.formGroup.currentValue &&
      changes.formGroup.previousValue !== changes.formGroup.currentValue
    ) {
      changes.formGroup.currentValue.setControl(this.property.Key, this.propertyForm, { emitEvent: false })
    }
    if (changes.property && this.property) {
      this.updatePropertyForm()
    }
  }

  ngOnDestroy() {
    if (this.propertyForm) {
      // If the property form is invalid, we should remove it from the form group to avoid memory leaking the property & validators
      if (this.propertyForm.invalid) {
        this.formGroup.removeControl(this.property.Key, { emitEvent: true })
      }
    }
  }

  onInput() {
    this.propertyForm.markAsDirty()
  }

  onFocus() {
    this.hasFocus = true
  }

  onBlur() {
    this.hasFocus = false
  }

  updatePropertyForm() {
    /*
     * We don't update dirty fields if it has focus.
     * This is way we give user option to finish the input
     * Invalid value will be replaced, as it can be changed by other means, like selecting value from the diagram.
     * */
    if (!(this.propertyForm.dirty && this.hasFocus)) {
      // Format the number according to the current locale and replace the property form control value with it.
      // (The value will be parsed into a number when edited)
      const formattedValue = this.localizeNumberPropertyPipe.transform(this.property)
      this.parsedValueCache[formattedValue] = typeof this.property.Value === 'number' ? this.property.Value : Number(this.property.Value)
      this.propertyForm.setValue(formattedValue, { emitEvent: false })
      this.propertyForm.markAsPristine()
    }
    if (this.property.IsEnabled) {
      this.propertyForm.enable({ emitEvent: false })
    } else {
      this.propertyForm.disable({ emitEvent: false })
    }

    // Update validators
    const validators: ValidatorFn[] = []

    // Ensure the value is numeric
    validators.push(this.internalValidatorNumeric.bind(this))

    if (this.property.IsRequired) {
      validators.push(Validators.required)
    }
    if (this.property.MinValue) {
      validators.push(this.internalValidatorMin.bind(this))
    }
    if (this.property.MaxValue) {
      validators.push(this.internalValidatorMax.bind(this))
    }

    this.propertyForm.clearValidators()
    this.propertyForm.setValidators(validators)
    this.propertyForm.updateValueAndValidity({ emitEvent: false })

    this.toolTipTextId = this.entityService.getMetadataValue(this.property, 'TooltipTextId')
  }

  private parseNumberInput(input: string): number | null {
    if (typeof input !== 'string' || input === '') {
      return Number.NaN
    }

    // Use the cached if available., this greatly increases the performance of the validator functions
    if (this.parsedValueCache[input] !== undefined) {
      return this.parsedValueCache[input]
    }

    // Modify the string by removing whitespace and replacing locale-specific characters with the JS characters
    const invariantString = input
      .trim()
      .replace(/\s/g, '')
      .replaceAll(getLocaleNumberSymbol(this.translateService.currentLang() ?? 'en', NumberSymbol.MinusSign), '-')
      .replaceAll(getLocaleNumberSymbol(this.translateService.currentLang() ?? 'en', NumberSymbol.Group), '')
      .replaceAll(getLocaleNumberSymbol(this.translateService.currentLang() ?? 'en', NumberSymbol.Decimal), '.')

    // If the property has no visible decimals, round the value to the nearest integer. Note that we must still use parseFloat()
    // to ensure the value is parsed correctly.
    // If the property does have decimals, we'll round the result to the needed precision.

    if (!this.property.Decimals) {
      const result = Math.round(parseFloat(invariantString))
      this.parsedValueCache[input] = result
      return result
    }

    const floatValue = parseFloat(invariantString)
    const decimalFactor = parseFloat(`1e${this.property.Decimals}`)

    // Round the value to the decimal precision defined in the property data
    const result = Math.round((floatValue + Number.EPSILON) * decimalFactor) / decimalFactor

    this.parsedValueCache[input] = result

    return result
  }

  private internalValidatorMin(control: FormControl<string>): ValidationErrors {
    if (control.value) {
      const numericValue = this.parseNumberInput(control.value)
      if (!Number.isNaN(numericValue) && numericValue < this.property.MinValue) {
        return { min: { min: this.property.MinValue, actual: numericValue } }
      }
    }
    return null
  }

  private internalValidatorMax(control: FormControl<string>): ValidationErrors {
    if (control.value) {
      const numericValue = this.parseNumberInput(control.value)
      if (!Number.isNaN(numericValue) && numericValue > this.property.MaxValue) {
        return { max: { max: this.property.MaxValue, actual: numericValue } }
      }
    }
    return null
  }

  private internalValidatorNumeric(control: FormControl<string>): ValidationErrors {
    // Regex to do basic verification for a numeric value, valid characters for the regex are:
    // First character may be a minus (One of: 0x2212 for official minus symbol, 0x2010 for hyphen-minus, hyphen)
    // Following characters can be any digit, dot, comma, space and non-breaking space (0x0202F)
    const validatorRegex = /^[-\u2212\u2010]?[0-9,.\s\u202F]+$/

    const { value } = control
    if (value === null || value === undefined || typeof value !== 'string') {
      return null
    }

    if (!validatorRegex.test(value)) {
      return { numeric: { isNumeric: false } }
    }

    return null
  }
}
